import { SerializeQueryArgs } from '@reduxjs/toolkit/dist/query/defaultSerializeQueryArgs';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { ESeconds } from 'modules/common/constants/const';

export enum RequestType {
  BoundAccounts = 'BoundAccounts',
  BoundEmails = 'BoundEmails',
  MyBundles = 'MyBundles',
  MySubscriptions = 'MySubscriptions',
  Notifications = 'Notifications',
  NotificationsChannel = 'NotificationsChannel',
  ProjectWhitelist = 'ProjectWhitelist',
  ReferralCodes = 'ReferralCodes',
  RewardBalance = 'RewardBalance',
  RewardTxs = 'RewardTxs',
  TFAStatus = 'TFAStatus',
  TeamAccountDetails = 'TeamAccountDetails',
  TeamAccounts = 'TeamAccounts',
  TeamCreationPermission = 'TeamCreationPermission',
  TeamInvitations = 'TeamInvitations',
  TelegramNotifications = 'TelegramNotifications',
  ToSStatus = 'ToSStatus',
  ProfileConfig = 'ProfileConfig',
  Sessions = 'Sessions',
  UserMethods = 'UserMethods',
  UserRateLimit = 'UserRateLimit',
}

// Endpoints that must be cached by their names and params should be listed here
// Please keep in mind that if the name of an endpoint has changed,
// this list should also be updated
const endpointsSerializedByParams = [
  'checkDealDeposit',
  'checkPAYGDeposit',
  'fetchAPIKeys',
  'fetchAccountStatus',
  'fetchAllowedJWTsCount',
  'fetchAssociatedAccounts',
  'fetchBalance',
  'fetchBlockchainTxData',
  'fetchBlockchains',
  'fetchBoundEmails',
  'fetchBundlePaymentPlans',
  'fetchChainTimeframeData',
  'fetchGoogleOAuthURL',
  'fetchIsAccountCreationAllowed',
  'fetchIsOldAccount',
  'fetchJWTStatus',
  'fetchJWTs',
  'fetchMyBundles',
  'fetchMyBundlesStatus',
  'fetchNativeTokenPrice',
  'fetchNotifications',
  'fetchOAuthAccountJWT',
  'fetchOldAccountJWT',
  'fetchOpenAPIDocumentJSON',
  'fetchOpenAPIDocumentYAML',
  'fetchProjectTotalRequests',
  'fetchProjectWhitelist',
  'fetchProjectsWhitelist',
  'fetchProjectWhitelistBlockchains',
  'fetchProjectsWhitelistsBlockchains',
  'fetchUserMethods',
  'fetchUserRateLimit',
  'fetchPromoBanner',
  'fetchRPBanner',
  'fetchRawJWTs',
  'fetchReferralCodes',
  'fetchReferralLinksByCodes',
  'fetchReferralPrograms',
  'fetchReferralsCount',
  'fetchReferrer',
  'fetchRewardBalance',
  'fetchRewardTxs',
  'fetchStandardAccountJWT',
  'fetchStories',
  'fetchTFAStatus',
  'fetchTeamAccountDetails',
  'fetchTeamAccountJWT',
  'fetchTeamAccounts',
  'fetchToSStatus',
  'fetchTopBanners',
  'fetchUserETHAddresses',
  'fetchAvailableLoginProviders',

  /* stats */
  'fetchPrivateStats',
  'fetchPublicRequestsCountStats',
  'fetchPublicStats',
  'fetchRawRequestsTelemetry',

  /* billing usage */
  'fetchUserSpendings',
  'fetchUserSpendingAggregated',

  /* notifications */
  'fetchNotifications',
  'fetchNotificationsChannels',

  /* invoices */
  'checkOrGenerateDepositInvoice',
  'getCryptoDepositInvoiceDocument',
  'getStripeDepositInvoiceDocument',

  'fetchSlackNotificationsBotDetails',

  'getUIProfileConfig',

  /* shark status */
  'fetchUptime',
  'fetchComponents',
  'fetchBlockchainsByAddress',
];

const unserializedFields = ['totp', 'forceFetchInternal'];

// filters 2fa code to not include it to the cache key
const filterUnserializedParams = (entiries: [string, unknown][]) =>
  entiries.filter(([key]) => !unserializedFields.includes(key));

const sortQueryArgsKeys = (queryArgs: unknown) => {
  if (typeof queryArgs === 'undefined') {
    return {}; // to avoid double requesting for undefined and an empty object
  }

  if (queryArgs && typeof queryArgs === 'object') {
    // to make sure that a cache key doesn't depend on the query args keys order
    return Object.fromEntries(
      filterUnserializedParams(Object.entries(queryArgs).sort()),
    );
  }

  return queryArgs;
};

const processFetchStoriesArgs = (queryArgs: unknown) => {
  if (queryArgs && typeof queryArgs === 'object') {
    // omit searchQuery if it is empty
    const args = Object.fromEntries(
      Object.entries(queryArgs).filter(
        ([key, value]) => !(key === 'searchQuery' && !value),
      ),
    );

    // omit args if it is empty
    return Object.keys(args).length === 0 ? undefined : args;
  }
};

const serializeQueryArgs: SerializeQueryArgs<unknown> = ({
  endpointName,
  queryArgs,
}) => {
  if (endpointsSerializedByParams.includes(endpointName)) {
    const sortedArgs = sortQueryArgsKeys(queryArgs);

    const processedArgs =
      endpointName === 'fetchStories'
        ? processFetchStoriesArgs(sortedArgs)
        : sortedArgs;

    return endpointName + JSON.stringify({ queryArgs: processedArgs });
  }

  return endpointName;
};

export const web3Api = createApi({
  baseQuery: fakeBaseQuery(),
  endpoints: () => ({}),
  // To keep endpont data after all subscribed to the endpoint components
  // are unmounted. Needs to reduce amount of requests
  keepUnusedDataFor: ESeconds.Minute,
  // To not refetch data automatically. Needs to reduce amount of requests
  refetchOnMountOrArgChange: false,
  serializeQueryArgs,
  tagTypes: [...Object.values(RequestType)],
});
